import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, X, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import toast from "react-hot-toast";

function notificationNeedsAck(notification) {
  return !!(notification?.require_ack || String(notification?.ack_question || "").trim());
}

export default function CustomMessagePopup({ show, notification, onClose, onAcknowledge }) {
  const navigate = useNavigate();
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const question = String(notification?.ack_question || "").trim();
  const needsAck = notificationNeedsAck(notification);

  useEffect(() => {
    setAnswer("");
    setSubmitting(false);
  }, [notification?.id, notification?._id]);

  if (!notification) return null;

  const pageLink = String(notification.link || "").trim();
  const hasPageLink = Boolean(pageLink && pageLink !== "/");

  const handleClose = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (needsAck) return;
    onClose?.();
  };

  const handleOpenLink = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const url = hasPageLink
      ? (pageLink.startsWith("http") || pageLink.startsWith("/")
          ? pageLink
          : createPageUrl(pageLink))
      : "";

    if (needsAck) {
      if (question && !answer.trim()) {
        toast.error("יש לענות על השאלה");
        return;
      }
      setSubmitting(true);
      try {
        await onAcknowledge?.(answer.trim());
      } catch (err) {
        toast.error(err?.response?.data?.message || "שגיאה באישור הקריאה");
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    } else {
      onClose?.();
    }

    if (!url) return;
    const current = `${window.location.pathname}${window.location.search}`;
    if (current !== url) navigate(url);
  };

  const handleAcknowledge = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (question && !answer.trim()) {
      toast.error("יש לענות על השאלה");
      return;
    }
    setSubmitting(true);
    try {
      await onAcknowledge?.(answer.trim());
    } catch (err) {
      toast.error(err?.response?.data?.message || "שגיאה באישור הקריאה");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/45 p-0 sm:p-4"
          dir="rtl"
          onClick={handleClose}
        >
          <motion.div
            initial={{ y: 56, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 320 }}
            className="relative w-full max-w-md overflow-hidden rounded-t-3xl sm:rounded-3xl bg-[#f7faf8] shadow-[0_24px_60px_rgba(15,40,30,0.28)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="custom-message-popup-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-l from-emerald-500 via-teal-500 to-emerald-700" />

            {!needsAck && (
              <button
                type="button"
                onClick={handleClose}
                aria-label="סגור"
                className="absolute left-3 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-slate-500 transition hover:bg-white hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            <div className="px-6 pb-6 pt-8">
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.08, type: "spring", stiffness: 260, damping: 18 }}
                className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-700/20"
              >
                <Megaphone className="h-7 w-7" />
              </motion.div>

              <p className="mb-2 text-center text-[11px] font-semibold tracking-[0.18em] text-emerald-700/80">
                {needsAck ? "חובה לאשר קריאה" : "הודעה חדשה"}
              </p>

              <h2
                id="custom-message-popup-title"
                className="text-center text-2xl font-bold leading-snug text-slate-900"
              >
                {notification.title}
              </h2>

              <p className="mt-3 whitespace-pre-wrap text-center text-[15px] leading-7 text-slate-600">
                {notification.message}
              </p>

              {question ? (
                <div className="mt-5 space-y-2 text-right">
                  <p className="text-sm font-semibold text-slate-800">{question}</p>
                  <Textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    rows={1}
                    placeholder="כתוב כאן את התשובה..."
                    className="bg-white min-h-0 h-11 resize-none py-2"
                  />
                </div>
              ) : null}

              <div className="mt-7 flex flex-col gap-2.5">
                {hasPageLink ? (
                  <Button
                    type="button"
                    className="h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
                    onClick={handleOpenLink}
                  >
                    מעבר לדף
                    <ArrowLeft className="mr-2 h-4 w-4" />
                  </Button>
                ) : null}
                {needsAck ? (
                  <Button
                    type="button"
                    className={
                      hasPageLink
                        ? "h-11 w-full rounded-xl text-slate-700 hover:bg-white hover:text-slate-900"
                        : "h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
                    }
                    variant={hasPageLink ? "ghost" : "default"}
                    onClick={handleAcknowledge}
                    disabled={submitting}
                  >
                    {submitting ? "שולח..." : question ? "שלח תשובה" : "אישור שקראתי"}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className={
                      hasPageLink
                        ? "h-11 w-full rounded-xl text-slate-600 hover:bg-white hover:text-slate-900"
                        : "h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
                    }
                    variant={hasPageLink ? "ghost" : "default"}
                    onClick={handleClose}
                  >
                    הבנתי
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
